<script setup lang="ts">
import { useCvButtonNavigation } from '../useCvButtonNavigation';
import PartnerRakutenHeaderMenu from './PartnerRakutenHeaderMenu.vue';
import LogoForRakutenShokenSp from './logo-for-rakuten-shoken-sp.svg';
import LogoForRakutenShoken from './logo-for-rakuten-shoken.svg';
import NuxtLinkFlat from 'theme/components/atoms/NuxtLinkFlat.vue';
import { useDeviceType } from 'theme/composables/useDeviceType';
import { ref, watch } from 'vue';

const items = [
  {
    to: '#service',
    text: 'サービスについて',
  },
  {
    to: '#introduction',
    text: 'IFAご紹介',
  },
  {
    to: '#case',
    text: '相談事例',
  },
  {
    to: '#voice',
    text: '体験者の声',
  },
];

const isActive = ref(false);
const toggleMenu = () => (isActive.value = !isActive.value);
const route = useRoute();
const routeFullPath = computed(() => route.fullPath);
watch(routeFullPath, () => {
  isActive.value = false;
});
const { isDesktop } = useDeviceType();

const { cvButtonTo } = useCvButtonNavigation();
</script>

<template>
  <header id="header" class="header">
    <h1 class="header__logo logo">
      <a href="#header" class="logo__wrap">
        <img
          :src="isDesktop ? LogoForRakutenShoken : LogoForRakutenShokenSp"
          alt="オカネコ for 楽天証券"
          class="logo__icon"
        />
      </a>
    </h1>

    <div class="header__toggle toggle" @click="toggleMenu">
      <img src="./menu.svg" alt="menu" class="toggle__icon" />
    </div>

    <PartnerRakutenHeaderMenu
      :is-active="isActive"
      class="header__mobile-menu"
      @close="toggleMenu"
    />

    <nav class="header__nav nav">
      <ul class="nav__list list">
        <li v-for="item in items" :key="item.to" class="list__item item">
          <a :to="item.to" class="item__link link">{{ item.text }}</a>
        </li>
      </ul>
    </nav>

    <div v-if="isDesktop">
      <NuxtLinkFlat
        class="header__button button"
        :to="cvButtonTo"
        variant="conversion"
        >まずは診断</NuxtLinkFlat
      >
    </div>
  </header>
</template>

<style lang="scss" scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 68px;
  padding: 0 16px;
  gap: 32px;

  @include desktop-only {
    max-width: 1520px;
    height: 132px;
    margin: auto;
    padding: 0 40px;
  }

  &__logo {
    transform: translateY(1px);

    @include desktop-only {
      transform: translateY(3px);
    }
  }

  &__toggle {
    @include desktop-only {
      display: none;
    }
  }

  &__mobile-menu {
    z-index: 1;
    position: fixed;
    top: 0;
    left: 0;
  }

  &__nav {
    display: none;

    @include desktop-only {
      display: inline-block;
      flex: 0 0 auto;
      margin-left: auto;
    }
  }

  // FIXME: 本番環境でのみButtonFlatStyleの `display: inline-flex` が優先されてしまう問題がある
  // 何らかのバグである可能性が高いため、 `!important` による修正はあくまでその場しのぎ
  // cf. https://scrum.kagam.in/project/asuka-okane-kenko40/us/318?no-milestone=1
  &__button {
    display: none;

    @include desktop-only {
      display: inline-flex;
      flex: 0 0 auto;
    }
  }
}

.logo {
  &__wrap {
    display: flex;
    align-items: flex-start;
  }

  &__icon {
    display: inline-block;
    width: auto;
    min-width: 210px;
    height: 48px;
    margin: 0 4px;
    max-width: 100%;

    @include desktop-only {
      height: 34px;
      margin: 0 8px;
    }
  }
}

.list {
  display: flex;
  gap: 32px;
  grid-template-columns: repeat(auto-fit, minmax(8em, max-content));
  align-items: center;
  justify-content: flex-end;
}

.link {
  font-size: 16px;
  font-weight: bold;
  color: $color-text-and-object-high-emphasis;
}

.button {
  white-space: nowrap;
}
</style>
